import { bn } from './strings/bn';
import { en } from './strings/en';
import { gu } from './strings/gu';
import { hi } from './strings/hi';
import { kn } from './strings/kn';
import { ml } from './strings/ml';
import { mr } from './strings/mr';
import { or } from './strings/or';
import { pa } from './strings/pa';
import { ta } from './strings/ta';
import { te } from './strings/te';

/**
 * The patient page in English and ten Indian languages.
 *
 * Only the page's own words are translated. What the dentist writes - their
 * message - is shown exactly as written; the dentist chooses the patient's
 * language when sharing and writes accordingly.
 *
 * The translations were written for this page and should be read by a native
 * speaker before launch: a medical instruction that reads oddly is worse than
 * one in English.
 */
export interface PatientStrings {
  titleOne: string;
  titleMany: string;
  tooth: string;
  teeth: string;
  both: string;
  overview: string;
  front: string;
  leftSide: string;
  rightSide: string;
  bitingSurface: string;
  show: string;
  newCrown: string;
  newCrowns: string;
  yourTeeth: string;
  upperTeeth: string;
  lowerTeeth: string;
  hint: string;
  zoomIn: string;
  zoomOut: string;
  turnSlowly: string;
  stopTurning: string;
  backToStart: string;
  fullScreen: string;
  exitFullScreen: string;
  fromDentist: string;
  shadeNote: (shade: string) => string;
  before: string;
  after: string;
  seeInRoom: string;
  arNotSupported: string;
  appointmentTitle: string;
  confirm: string;
  confirmed: string;
  anotherTime: string;
  anotherTimeSent: string;
  language: string;
  largerText: string;
}

export const PATIENT_LANGUAGES = [
  { code: 'en', name: 'English', locale: 'en-IN' },
  { code: 'hi', name: 'हिन्दी', locale: 'hi-IN' },
  { code: 'bn', name: 'বাংলা', locale: 'bn-IN' },
  { code: 'mr', name: 'मराठी', locale: 'mr-IN' },
  { code: 'te', name: 'తెలుగు', locale: 'te-IN' },
  { code: 'ta', name: 'தமிழ்', locale: 'ta-IN' },
  { code: 'gu', name: 'ગુજરાતી', locale: 'gu-IN' },
  { code: 'kn', name: 'ಕನ್ನಡ', locale: 'kn-IN' },
  { code: 'ml', name: 'മലയാളം', locale: 'ml-IN' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ', locale: 'pa-IN' },
  { code: 'or', name: 'ଓଡ଼ିଆ', locale: 'or-IN' },
] as const;

export type PatientLanguage = (typeof PATIENT_LANGUAGES)[number]['code'];

const STRINGS: Record<PatientLanguage, PatientStrings> = {
  en,
  hi,
  bn,
  mr,
  te,
  ta,
  gu,
  kn,
  ml,
  pa,
  or,
};

export function patientStrings(language: PatientLanguage): PatientStrings {
  return STRINGS[language];
}

export function localeFor(language: PatientLanguage): string {
  return PATIENT_LANGUAGES.find((item) => item.code === language)?.locale ?? 'en-IN';
}

export function isPatientLanguage(value: string | null | undefined): value is PatientLanguage {
  return PATIENT_LANGUAGES.some((item) => item.code === value);
}

/**
 * The language to open in: the one the dentist chose for the link, else the
 * phone's own if it is one of ours, else English.
 */
export function resolveLanguage(chosen: string | null | undefined): PatientLanguage {
  if (isPatientLanguage(chosen)) return chosen;
  const phone = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : 'en';
  return isPatientLanguage(phone) ? phone : 'en';
}
